import type { Language } from '#src/languageService.js'

/**
 * Localized URL slugs, keyed by canonical (English) path **segment**.
 *
 * Each entry maps a language to that segment's slug in that language. A segment
 * not listed here is identical across languages — this covers dynamic content
 * slugs (`[postSlug]` values), route groups (`(home)`), and words that read the
 * same in every language (e.g. `portfolio`, `journal`, `ai`).
 *
 * These become permanent public URLs, so they are hard-coded here (not from the
 * CMS). Both the runtime path helpers (`languageService.localizePath` /
 * `delocalizePath`) and the `/sv` route generator (`scripts/genSv.ts`) read this
 * map, so the physical folders and the links always agree.
 *
 * NOTE: only STATIC route segments belong here. A dynamic CMS slug that happened
 * to equal one of these keys would also be translated, so keep keys distinct from
 * content slugs.
 *
 * TODO: verify the Swedish slugs with a native speaker before launch.
 */
export const ROUTE_SEGMENTS: Record<string, Partial<Record<Language, string>>> = {
  contact: { sv: 'kontakt' },
  'about-us': { sv: 'om-oss' },
  'studio-rental': { sv: 'studio-uthyrning' },
  // The rest are kept the same as English
}

/** Canonical (English) segment -> its slug in `lang` (identity when untranslated). */
export function localizeSegment(segment: string, lang: Language): string {
  return ROUTE_SEGMENTS[segment]?.[lang] ?? segment
}

// Reverse index: `${lang}:${localizedSlug}` -> canonical segment.
const canonicalBySlug = new Map<string, string>()
for (const [canonical, byLang] of Object.entries(ROUTE_SEGMENTS)) {
  for (const [lang, slug] of Object.entries(byLang)) {
    canonicalBySlug.set(`${lang}:${slug}`, canonical)
  }
}

/** A `lang` slug -> its canonical (English) segment (identity when unknown). */
export function canonicalizeSegment(slug: string, lang: Language): string {
  return canonicalBySlug.get(`${lang}:${slug}`) ?? slug
}

/**
 * Canonical paths that have real translated content per language.
 *
 * A localized (`/sv/...`) page NOT listed here shows fallback (English) content,
 * so it is marked `noindex` and dropped from the sitemap and hreflang to avoid
 * duplicate content. Add a canonical path here once its translation exists in the
 * CMS (see languageService.isTranslated). The default language is always indexable
 * and is not listed.
 */
export const TRANSLATED_PATHS: Partial<Record<Language, readonly string[]>> = {
  sv: [
    // Add each canonical path once its Swedish content exists in DatoCMS, e.g.:
    '/contact',
    '/about-us',
    '/services/studio-rental',
  ],
}
